import type { Coordinate, Drawing } from '@/types'
import type { FieldCoordinateSystem } from '@/utils'
import { pointToLineDistance } from '@/utils'

// Constants for proximity filtering
export const BEZIER_SAMPLE_POINTS = 10

/**
 * Sample points along a cubic bezier curve.
 */
export function sampleCubicBezier(
	p0: Coordinate,
	cp1: Coordinate,
	cp2: Coordinate,
	p3: Coordinate,
	numSamples: number,
): Coordinate[] {
	const samples: Coordinate[] = []
	for (let i = 0; i <= numSamples; i++) {
		const t = i / numSamples
		const t2 = t * t
		const t3 = t2 * t
		const mt = 1 - t
		const mt2 = mt * mt
		const mt3 = mt2 * mt

		samples.push({
			x: mt3 * p0.x + 3 * mt2 * t * cp1.x + 3 * mt * t2 * cp2.x + t3 * p3.x,
			y: mt3 * p0.y + 3 * mt2 * t * cp1.y + 3 * mt * t2 * cp2.y + t3 * p3.y,
		})
	}
	return samples
}

/**
 * Calculate minimum distance from a pixel position to any segment of a drawing.
 */
export function getDistanceToDrawingPath(
	pixelPos: Coordinate,
	drawing: Drawing,
	coordSystem: FieldCoordinateSystem,
): number {
	// Early return for empty drawings
	if (drawing.segments.length === 0) {
		return Infinity
	}

	let minDistance = Infinity

	for (const segment of drawing.segments) {
		const pointIds = segment.pointIds

		if (segment.type === 'line') {
			// Line: two points
			if (pointIds.length < 2) continue
			const p1 = drawing.points[pointIds[0]!]
			const p2 = drawing.points[pointIds[1]!]
			if (!p1 || !p2) continue

			const pixel1 = coordSystem.feetToPixels(p1.x, p1.y)
			const pixel2 = coordSystem.feetToPixels(p2.x, p2.y)
			const dist = pointToLineDistance(pixelPos, pixel1, pixel2)
			minDistance = Math.min(minDistance, dist)

		} else if (segment.type === 'cubic') {
			// Handle both NEW and OLD formats
			if (pointIds.length === 2) {
				// NEW FORMAT: handles in nodes
				const fromPoint = drawing.points[pointIds[0]!]
				const toPoint = drawing.points[pointIds[1]!]
				if (!fromPoint || !toPoint) continue

				// If handles exist, sample the bezier curve
				if (fromPoint.handleOut && toPoint.handleIn) {
					const p0 = coordSystem.feetToPixels(fromPoint.x, fromPoint.y)
					const cp1X = fromPoint.x + fromPoint.handleOut.x
					const cp1Y = fromPoint.y + fromPoint.handleOut.y
					const cp1 = coordSystem.feetToPixels(cp1X, cp1Y)
					const cp2X = toPoint.x + toPoint.handleIn.x
					const cp2Y = toPoint.y + toPoint.handleIn.y
					const cp2 = coordSystem.feetToPixels(cp2X, cp2Y)
					const p3 = coordSystem.feetToPixels(toPoint.x, toPoint.y)

					const samples = sampleCubicBezier(
						p0,
						cp1,
						cp2,
						p3,
						BEZIER_SAMPLE_POINTS,
					)

					for (let i = 0; i < samples.length - 1; i++) {
						const sample = samples[i]
						const nextSample = samples[i + 1]
						if (!sample || !nextSample) continue
						const dist = pointToLineDistance(pixelPos, sample, nextSample)
						minDistance = Math.min(minDistance, dist)
					}
				} else {
					// Fallback to straight line
					const pixel1 = coordSystem.feetToPixels(fromPoint.x, fromPoint.y)
					const pixel2 = coordSystem.feetToPixels(toPoint.x, toPoint.y)
					const dist = pointToLineDistance(pixelPos, pixel1, pixel2)
					minDistance = Math.min(minDistance, dist)
				}
			} else if (pointIds.length >= 4) {
				// OLD FORMAT: separate control points (4 points: start, cp1, cp2, end)
				const p0 = drawing.points[pointIds[0]!]
				const cp1 = drawing.points[pointIds[1]!]
				const cp2 = drawing.points[pointIds[2]!]
				const p3 = drawing.points[pointIds[3]!]
				if (!p0 || !cp1 || !cp2 || !p3) continue

				const pixel0 = coordSystem.feetToPixels(p0.x, p0.y)
				const pixelCp1 = coordSystem.feetToPixels(cp1.x, cp1.y)
				const pixelCp2 = coordSystem.feetToPixels(cp2.x, cp2.y)
				const pixel3 = coordSystem.feetToPixels(p3.x, p3.y)

				const samples = sampleCubicBezier(
					pixel0,
					pixelCp1,
					pixelCp2,
					pixel3,
					BEZIER_SAMPLE_POINTS,
				)

				for (let i = 0; i < samples.length - 1; i++) {
					const sample = samples[i]
					const nextSample = samples[i + 1]
					if (!sample || !nextSample) continue
					const dist = pointToLineDistance(pixelPos, sample, nextSample)
					minDistance = Math.min(minDistance, dist)
				}
			}
		} else if (segment.type === 'quadratic') {
			// Approximate quadratic as line for distance calculation
			if (pointIds.length < 2) continue
			const control = drawing.points[pointIds[0]!]
			const end = drawing.points[pointIds[1]!]
			if (!control || !end) continue

			const pixel1 = coordSystem.feetToPixels(control.x, control.y)
			const pixel2 = coordSystem.feetToPixels(end.x, end.y)
			const dist = pointToLineDistance(pixelPos, pixel1, pixel2)
			minDistance = Math.min(minDistance, dist)
		}
	}

	return minDistance
}
